import { useState, useRef, useEffect } from "react";
import AiCharacter from "../components/AiCharacter";
import { bffApi, createMessage, handleBFFError } from "../services/bffApi";
import { ExpandableBadge } from "../components/Badge/Badge";

const { REACT_APP_GOOGLE_MAPS_API_KEY, REACT_APP_DEFAULT_MODEL } = process.env;

const Assistant = () => {
  const [messages, setMessages] = useState([]);
  const [inputMessage, setInputMessage] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [conversationId, setConversationId] = useState(null);

  const [apiKey, setApiKey] = useState(() => {
    // Try to load from localStorage first, then fall back to env variable
    const savedApiKey = localStorage.getItem("tenoro_api_key");
    return savedApiKey || "";
  });
  const [mcpStatus, setMcpStatus] = useState(null);

  // Check if debug query parameter exists and has some value
  const urlParams = new URLSearchParams(window.location.search);
  const debugParam = urlParams.get("debug");
  const modelParam = urlParams.get("model");
  const promptName = urlParams.get("prompt");
  const [showApiKeyInput, setShowApiKeyInput] = useState(
    debugParam && debugParam.trim(),
  );
  const DEFAULT_MODEL = REACT_APP_DEFAULT_MODEL || "default";
  const [selectedModel, setSelectedModel] = useState(
    modelParam || DEFAULT_MODEL,
  );
  const [hasStartedChat, setHasStartedChat] = useState(false);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);
  const models = [
    { value: "gemini-2.5-flash-lite", label: "gemini-2.5-flash-lite" },
    { value: "gemini-2.5-flash", label: "gemini-2.5-flash" },
    { value: "gemini-2.5-pro", label: "gemini-2.5-pro" },
    { value: DEFAULT_MODEL, label: DEFAULT_MODEL },
  ];

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  useEffect(() => {
    if (inputRef.current && !hasStartedChat) {
      inputRef.current.focus();
    }
  }, [hasStartedChat]);

  // Check BFF health and MCP status on component mount
  useEffect(() => {
    const checkBFFStatus = async () => {
      try {
        // Check MCP status
        const status = await bffApi.getMCPStatus({
          apiKey,
        });
        setMcpStatus(status);
        console.log("MCP Status:", status);
      } catch (error) {
        console.warn("Failed to check BFF status:", handleBFFError(error));
      }
    };

    checkBFFStatus();
  }, []);

  const handleApiKeySubmit = () => {
    if (apiKey.trim()) {
      // Save API key to localStorage
      localStorage.setItem("tenoro_api_key", apiKey.trim());
      setShowApiKeyInput(false);
    }
  };

  const handleApiKeyChange = (newApiKey) => {
    setApiKey(newApiKey);
    // Save to localStorage whenever the API key changes
    if (newApiKey.trim()) {
      localStorage.setItem("tenoro_api_key", newApiKey.trim());
    }
  };

  const clearApiKey = () => {
    setApiKey("");
    localStorage.removeItem("tenoro_api_key");
    setShowApiKeyInput(true);
  };

  const sendMessage = async () => {
    if (!inputMessage.trim() || isLoading) return;

    const userMessage = createMessage({
      text: inputMessage,
      isBot: false,
    });

    setMessages((prev) => [...prev, userMessage]);
    const currentInput = inputMessage;
    setInputMessage("");
    setIsLoading(true);

    if (!hasStartedChat) {
      setHasStartedChat(true);
    }

    try {
      // Create the message object to send to the backend
      const messageToSend = {
        role: "user",
        content: currentInput,
      };

      // Make request to BFF - backend handles tool execution automatically
      const completion = await bffApi.chatCompletion({
        conversationId,
        apiKey,
        prompt: promptName,
        message: messageToSend,
        model: selectedModel,
      });
      console.log("message received", completion);

      // Update conversation ID if this is a new conversation
      if (completion.conversationId && !conversationId) {
        setConversationId(completion.conversationId);
        console.log(
          `💬 Started new conversation: ${completion.conversationId}`,
        );
      }

      const { response, map, image, badge } = completion;

      if (response) {
        const botMessage = createMessage({
          text: response,
          isBot: true,
          map: map || null,
          image: image || null,
          badge: badge || null,
        });

        setMessages((prev) => [...prev, botMessage]);
        console.log("✅ Received response from assistant");
      }
    } catch (error) {
      console.error("Error calling BFF:", error);
      const errorMessage = createMessage({
        text: `Sorry, I encountered an error: ${handleBFFError(error)}. Please check your connection and try again.`,
        isBot: true,
        isError: true,
      });
      setMessages((prev) => [...prev, errorMessage]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  const startNewChat = () => {
    setMessages([]);
    setHasStartedChat(false);
    setInputMessage("");
    setConversationId(null);
  };

  if (showApiKeyInput) {
    return (
      <div
        style={{
          minHeight: "100vh",
          background: "linear-gradient(135deg, #000000 0%, #1a1a1a 100%)",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          padding: "20px",
        }}
      >
        <div
          style={{
            background: "rgba(34, 34, 34, 0.95)",
            padding: "40px",
            borderRadius: "16px",
            border: "1px solid #6c50e9",
            boxShadow: "0 20px 40px rgba(108, 80, 233, 0.1)",
            maxWidth: "400px",
            width: "100%",
            textAlign: "center",
          }}
        >
          <h2
            style={{
              color: "#6c50e9",
              marginBottom: "20px",
              fontSize: "24px",
              fontWeight: "bold",
            }}
          >
            Tenoro
          </h2>
          <p
            style={{
              color: "#cccccc",
              marginBottom: "25px",
              fontSize: "14px",
            }}
          >
            {localStorage.getItem("tenoro_api_key")
              ? "API key loaded from storage. You can update it here if needed."
              : "Enter your OpenAI API key to start chatting"}
          </p>
          <input
            type="password"
            value={apiKey}
            onChange={(e) => handleApiKeyChange(e.target.value)}
            placeholder="sk-..."
            style={{
              width: "100%",
              padding: "12px 16px",
              backgroundColor: "#222222",
              border: "1px solid #444444",
              borderRadius: "8px",
              color: "#ffffff",
              fontSize: "14px",
              marginBottom: "20px",
              outline: "none",
              transition: "all 0.3s ease",
              boxSizing: "border-box",
            }}
            onFocus={(e) => (e.target.style.borderColor = "#6c50e9")}
            onBlur={(e) => (e.target.style.borderColor = "#444444")}
            onKeyPress={(e) => e.key === "Enter" && handleApiKeySubmit()}
          />
          <button
            onClick={handleApiKeySubmit}
            disabled={!apiKey.trim()}
            style={{
              width: "100%",
              padding: "12px 20px",
              backgroundColor: apiKey.trim() ? "#6c50e9" : "#333333",
              color: apiKey.trim() ? "#ffffff" : "#666666",
              border: "none",
              borderRadius: "8px",
              fontSize: "16px",
              fontWeight: "bold",
              cursor: apiKey.trim() ? "pointer" : "not-allowed",
              transition: "all 0.3s ease",
            }}
          >
            Start Chatting
          </button>
        </div>
      </div>
    );
  }

  // Welcome Screen (ChatGPT style)
  if (!hasStartedChat && messages.length === 0) {
    return (
      <div
        style={{
          minHeight: "100vh",
          background: "linear-gradient(135deg, #000000 0%, #1a1a1a 100%)",
          display: "flex",
          flexDirection: "column",
        }}
      >
        {/* Main Content */}
        <div
          style={{
            flex: 1,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            padding: "40px 20px",
            maxWidth: "800px",
            margin: "0 auto",
            width: "100%",
          }}
        >
          <div style={{ textAlign: "center", marginBottom: "60px" }}>
            <div style={{ marginBottom: "20px" }}>
              <AiCharacter isAnimating={false} />
            </div>
            <h1
              style={{
                fontSize: "48px",
                fontWeight: "600",
                color: "#ffffff",
                margin: "20px 0 10px",
                background: "linear-gradient(135deg, #6c50e9, #9783f0)",
                WebkitBackgroundClip: "text",
                WebkitTextFillColor: "transparent",
                backgroundClip: "text",
              }}
            >
              Tenoro
            </h1>
            <p
              style={{
                fontSize: "18px",
                color: "#cccccc",
                margin: "0 0 20px",
              }}
            >
              I am Tenoro, your AI assistant with advanced capabilities. How can
              I help you today?
            </p>

            {/* MCP Status Indicator */}
            {mcpStatus && (
              <div
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: "8px",
                  padding: "8px 12px",
                  backgroundColor: mcpStatus.connected
                    ? "rgba(34, 197, 94, 0.1)"
                    : "rgba(239, 68, 68, 0.1)",
                  border: `1px solid ${
                    mcpStatus.connected
                      ? "rgba(34, 197, 94, 0.3)"
                      : "rgba(239, 68, 68, 0.3)"
                  }`,
                  borderRadius: "16px",
                  fontSize: "12px",
                  color: mcpStatus.connected ? "#22c55e" : "#ef4444",
                  marginTop: "10px",
                }}
              >
                <div
                  style={{
                    width: "6px",
                    height: "6px",
                    borderRadius: "50%",
                    backgroundColor: mcpStatus.connected
                      ? "#22c55e"
                      : "#ef4444",
                  }}
                />
                {mcpStatus.connected
                  ? `Enhanced with ${mcpStatus.tools?.length || 0} tools`
                  : "Basic mode"}
              </div>
            )}
          </div>

          {/* Input Area */}
          <div
            style={{
              width: "100%",
              maxWidth: "700px",
              position: "relative",
            }}
          >
            <div
              style={{
                position: "relative",
                display: "flex",
                alignItems: "flex-end",
                gap: "12px",
                padding: "16px",
                backgroundColor: "rgba(34, 34, 34, 0.8)",
                borderRadius: "24px",
                border: "1px solid #444444",
                backdropFilter: "blur(10px)",
              }}
            >
              <textarea
                ref={inputRef}
                value={inputMessage}
                onChange={(e) => setInputMessage(e.target.value)}
                onKeyPress={handleKeyPress}
                placeholder="Message Tenoro..."
                disabled={isLoading}
                style={{
                  flex: 1,
                  minHeight: "24px",
                  maxHeight: "120px",
                  padding: "0",
                  backgroundColor: "transparent",
                  border: "none",
                  color: "#ffffff",
                  fontSize: "16px",
                  resize: "none",
                  outline: "none",
                  fontFamily: "inherit",
                  lineHeight: "1.5",
                }}
              />
              <button
                onClick={sendMessage}
                disabled={!inputMessage.trim() || isLoading}
                style={{
                  width: "32px",
                  height: "32px",
                  backgroundColor:
                    inputMessage.trim() && !isLoading ? "#6c50e9" : "#444444",
                  color: "#ffffff",
                  border: "none",
                  borderRadius: "8px",
                  cursor:
                    inputMessage.trim() && !isLoading
                      ? "pointer"
                      : "not-allowed",
                  transition: "all 0.3s ease",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  flexShrink: 0,
                }}
              >
                {isLoading ? (
                  <div
                    style={{
                      width: "16px",
                      height: "16px",
                      border: "2px solid #ffffff",
                      borderTop: "2px solid transparent",
                      borderRadius: "50%",
                      animation: "spin 1s linear infinite",
                    }}
                  />
                ) : (
                  "↑"
                )}
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Chat View
  return (
    <div
      style={{
        minHeight: "100vh",
        background: "linear-gradient(135deg, #000000 0%, #1a1a1a 100%)",
        display: "flex",
        flexDirection: "column",
        position: "relative",
      }}
    >
      {/* Header */}
      <div
        style={{
          position: "fixed",
          top: 0,
          left: 0,
          right: 0,
          zIndex: 1000,
          background: "rgba(34, 34, 34, 0.95)",
          borderBottom: "1px solid #333333",
          padding: "16px 20px",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          backdropFilter: "blur(10px)",
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: "15px" }}>
          <AiCharacter isAnimating={isLoading} />
          <div>
            <h1
              style={{
                color: "#ffffff",
                margin: 0,
                fontSize: "18px",
                fontWeight: "600",
              }}
            >
              Tenoro Assistant
            </h1>
            {isLoading && (
              <p
                style={{
                  color: "#6c50e9",
                  margin: "2px 0 0",
                  fontSize: "12px",
                }}
              >
                🤖 Thinking...
              </p>
            )}
          </div>
        </div>

        <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
          <button
            onClick={startNewChat}
            style={{
              padding: "8px 16px",
              backgroundColor: "transparent",
              border: "1px solid #444444",
              borderRadius: "8px",
              color: "#cccccc",
              fontSize: "14px",
              cursor: "pointer",
              transition: "all 0.3s ease",
            }}
            onMouseEnter={(e) => {
              e.target.style.backgroundColor = "#444444";
              e.target.style.color = "#ffffff";
            }}
            onMouseLeave={(e) => {
              e.target.style.backgroundColor = "transparent";
              e.target.style.color = "#cccccc";
            }}
          >
            New Chat
          </button>

          {debugParam && debugParam.trim() && (
            <>
              <select
                value={selectedModel}
                onChange={(e) => setSelectedModel(e.target.value)}
                style={{
                  padding: "8px 12px",
                  backgroundColor: "#222222",
                  border: "1px solid #444444",
                  borderRadius: "8px",
                  color: "#ffffff",
                  fontSize: "14px",
                  cursor: "pointer",
                  outline: "none",
                }}
              >
                {models.map((model) => (
                  <option
                    key={model.value}
                    value={model.value}
                    style={{ backgroundColor: "#222222" }}
                  >
                    {model.label}
                  </option>
                ))}
              </select>

              <button
                onClick={() => setShowApiKeyInput(true)}
                style={{
                  padding: "8px 16px",
                  backgroundColor: "transparent",
                  border: "1px solid #6c50e9",
                  borderRadius: "8px",
                  color: "#6c50e9",
                  fontSize: "12px",
                  cursor: "pointer",
                  transition: "all 0.3s ease",
                }}
                onMouseEnter={(e) => {
                  e.target.style.backgroundColor = "#6c50e9";
                  e.target.style.color = "#ffffff";
                }}
                onMouseLeave={(e) => {
                  e.target.style.backgroundColor = "transparent";
                  e.target.style.color = "#6c50e9";
                }}
              >
                {apiKey ? "Change API Key" : "Set API Key"}
              </button>
            </>
          )}

          {conversationId && (
            <div
              style={{
                fontSize: "12px",
                color: "#666666",
                fontFamily: "monospace",
              }}
            >
              ID: {conversationId.substring(0, 8)}...
            </div>
          )}
        </div>
      </div>

      {/* Chat Messages */}
      <div
        style={{
          position: "absolute",
          top: "80px",
          bottom: "120px",
          left: "0",
          right: "0",
          overflowY: "scroll",
          WebkitOverflowScrolling: "touch",
          touchAction: "pan-y",
          willChange: "scroll-position",
          transform: "translateZ(0)",
        }}
      >
        <div
          style={{
            maxWidth: "800px",
            margin: "0 auto",
            padding: "20px",
          }}
        >
          {messages.map((message) => (
            <div
              key={message.id}
              style={{
                display: "flex",
                marginBottom: "24px",
                justifyContent: message.isBot ? "flex-start" : "flex-end",
              }}
            >
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  maxWidth: "70%",
                  padding: "16px 20px",
                  borderRadius: message.isBot
                    ? "0 18px 18px 18px"
                    : "18px 0 18px 18px",
                  backgroundColor: message.isError
                    ? "rgba(239, 68, 68, 0.1)"
                    : message.isBot
                      ? "rgba(108, 80, 233, 0.1)"
                      : "#6c50e9",
                  border: message.isError
                    ? "1px solid rgba(239, 68, 68, 0.3)"
                    : message.isBot
                      ? "1px solid rgba(108, 80, 233, 0.3)"
                      : "none",
                  color: message.isError ? "#ef4444" : "#ffffff",
                  fontSize: "16px",
                  lineHeight: "1.6",
                  wordWrap: "break-word",
                }}
              >
                <div>{message.text}</div>
                <div>
                  {message.map && (
                    <div
                      style={{
                        marginTop: "12px",
                        borderRadius: "8px",
                        overflow: "hidden",
                      }}
                    >
                      <iframe
                        src={`https://www.google.com/maps/embed/v1/place?key=${REACT_APP_GOOGLE_MAPS_API_KEY}&q=${message.map}&zoom=15`}
                        width="300"
                        height="200"
                        style={{
                          border: 0,
                          borderRadius: "8px",
                        }}
                        allowFullScreen=""
                        loading="lazy"
                        referrerPolicy="no-referrer-when-downgrade"
                        title="Location Map"
                      />
                    </div>
                  )}

                  {message.image && (
                    <img
                      src={message.image}
                      style={{
                        marginTop: "12px",
                        borderRadius: "8px",
                        overflow: "hidden",
                      }}
                    ></img>
                  )}
                  {message.badge && (
                    <div
                      style={{
                        paddingTop: "12px",
                        display: "flex",
                        float: "inline-end",
                      }}
                    >
                      <ExpandableBadge {...message.badge} />
                    </div>
                  )}
                </div>
              </div>
            </div>
          ))}

          {isLoading && (
            <div
              style={{
                display: "flex",
                marginBottom: "24px",
                justifyContent: "flex-start",
              }}
            >
              <div
                style={{
                  maxWidth: "70%",
                  padding: "16px 20px",
                  borderRadius: "0 18px 18px 18px",
                  backgroundColor: "rgba(108, 80, 233, 0.1)",
                  border: "1px solid rgba(108, 80, 233, 0.3)",
                  display: "flex",
                  flexDirection: "column",
                  gap: "8px",
                }}
              >
                <div
                  style={{
                    display: "flex",
                    gap: "6px",
                    alignItems: "center",
                  }}
                >
                  <div
                    style={{
                      width: "8px",
                      height: "8px",
                      backgroundColor: "#6c50e9",
                      borderRadius: "50%",
                      animation: "bounce 1.4s ease-in-out infinite",
                    }}
                  ></div>
                  <div
                    style={{
                      width: "8px",
                      height: "8px",
                      backgroundColor: "#6c50e9",
                      borderRadius: "50%",
                      animation: "bounce 1.4s ease-in-out infinite 0.2s",
                    }}
                  ></div>
                  <div
                    style={{
                      width: "8px",
                      height: "8px",
                      backgroundColor: "#6c50e9",
                      borderRadius: "50%",
                      animation: "bounce 1.4s ease-in-out infinite 0.4s",
                    }}
                  ></div>
                </div>
                <div
                  style={{
                    fontSize: "12px",
                    color: "#6c50e9",
                    marginTop: "4px",
                  }}
                >
                  Processing your request...
                </div>
              </div>
            </div>
          )}
          <div ref={messagesEndRef} />
        </div>
      </div>

      {/* Input Area */}
      <div
        style={{
          position: "fixed",
          bottom: 0,
          left: 0,
          right: 0,
          padding: "20px",
          background: "linear-gradient(135deg, #000000 0%, #1a1a1a 100%)",
          borderTop: "1px solid #333333",
        }}
      >
        <div
          style={{
            maxWidth: "800px",
            margin: "0 auto",
          }}
        >
          <div
            style={{
              position: "relative",
              display: "flex",
              alignItems: "flex-end",
              gap: "12px",
              padding: "16px",
              backgroundColor: "rgba(34, 34, 34, 0.8)",
              borderRadius: "24px",
              border: "1px solid #444444",
              backdropFilter: "blur(10px)",
            }}
          >
            <textarea
              value={inputMessage}
              onChange={(e) => setInputMessage(e.target.value)}
              onKeyPress={handleKeyPress}
              placeholder="Message Tenoro..."
              disabled={isLoading}
              style={{
                flex: 1,
                minHeight: "24px",
                maxHeight: "120px",
                padding: "0",
                backgroundColor: "transparent",
                border: "none",
                color: "#ffffff",
                fontSize: "16px",
                resize: "none",
                outline: "none",
                fontFamily: "inherit",
                lineHeight: "1.5",
                opacity: isLoading ? 0.6 : 1,
              }}
            />
            <button
              onClick={sendMessage}
              disabled={!inputMessage.trim() || isLoading}
              style={{
                width: "32px",
                height: "32px",
                backgroundColor:
                  inputMessage.trim() && !isLoading ? "#6c50e9" : "#444444",
                color: "#ffffff",
                border: "none",
                borderRadius: "8px",
                cursor:
                  inputMessage.trim() && !isLoading ? "pointer" : "not-allowed",
                transition: "all 0.3s ease",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                flexShrink: 0,
              }}
            >
              {isLoading ? (
                <div
                  style={{
                    width: "16px",
                    height: "16px",
                    border: "2px solid #ffffff",
                    borderTop: "2px solid transparent",
                    borderRadius: "50%",
                    animation: "spin 1s linear infinite",
                  }}
                />
              ) : (
                "↑"
              )}
            </button>
          </div>
        </div>
      </div>

      <style>{`
        @keyframes bounce {
          0%, 80%, 100% {
            transform: scale(0);
            opacity: 0.5;
          }
          40% {
            transform: scale(1);
            opacity: 1;
          }
        }

        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }

        /* Scrollbar styling */
        ::-webkit-scrollbar {
          width: 6px;
        }

        ::-webkit-scrollbar-track {
          background: #1a1a1a;
        }

        ::-webkit-scrollbar-thumb {
          background: #6c50e9;
          border-radius: 3px;
        }

        ::-webkit-scrollbar-thumb:hover {
          background: #9783f0;
        }

        /* Mobile scrolling improvements */
        @media (max-width: 768px) {
          * {
            -webkit-overflow-scrolling: touch;
          }

          html, body {
            height: 100%;
            overflow: hidden;
            position: fixed;
            width: 100%;
          }
        }
      `}</style>
    </div>
  );
};

export default Assistant;
